import React, { useState, useEffect } from 'react';
import { Card, Tabs, Tag, Button, Dropdown, Select, Spin, Image, Popconfirm, message, Tooltip } from "antd";
import EditableRow from "./EditableRow";
import { PlusOutlined, CheckOutlined, SyncOutlined, RightOutlined, LoadingOutlined } from '@ant-design/icons';
import ContactList from './ContactList';
import Channels from './Channels';
import { CustomerDetailData } from "../../../api/ChannelCustomer";
import { useTranslation } from 'react-i18next';
import CustomerImgList from './CustomerImgList';
import { getCustomerPhotos, uploadCustomerPhoto, deleteCustomerPhoto, CustomerPhoto } from "../../../api/CustomerPhoto";
import CallReportFail from './CallReportFail';

// Tag tự cắt chữ kèm "…" khi quá dài, hover hiện đầy đủ
const EllipsisTag = ({ color, children }: { color?: string; children: React.ReactNode }) => (
    <Tooltip title={children}>
        <Tag color={color || "default"} className="mr-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap align-middle">
            {children}
        </Tag>
    </Tooltip>
);

interface Props {
    customer: CustomerDetailData | null;
    customerTags: { label: string; value: number; color?: string }[];
    careStatuses: { label: string; value: number; color?: string }[];
    occupations: { label: string; value: number }[];
    onUpdateField: (field: string, value: any) => Promise<void>;
    onUpdateAddress: (data: { province_code: string; ward_code: string; address: string }) => Promise<void>;
}

export default function LeftInformationPanel({ customer, customerTags, careStatuses, occupations, onUpdateField, onUpdateAddress }: Props) {
    const { t } = useTranslation();
    const [selectedTagValues, setSelectedTagValues] = useState<number[]>([]);
    const [updatingField, setUpdatingField] = useState<string | null>(null);

    // Hình ảnh của khách hàng (GET /be/customers/{id}/photos) hiển thị dưới phần Ghi chú
    const [customerPhotos, setCustomerPhotos] = useState<CustomerPhoto[]>([]);
    useEffect(() => {
        const cid = customer?.id;
        if (!cid) { setCustomerPhotos([]); return; }
        let cancelled = false;
        getCustomerPhotos(cid, { limit: 100 }).then((res) => {
            if (!cancelled && res.status) setCustomerPhotos(res.items);
        });
        return () => { cancelled = true; };
    }, [customer?.id]);

    // Tải lên ảnh mới (POST /be/customers/{id}/photos — photos[], ≤10MB/ảnh) và xóa ảnh (DELETE /be/customers/photos/{photo_id})
    const photoInputRef = React.useRef<HTMLInputElement>(null);
    const [uploadingPhotos, setUploadingPhotos] = useState(false);
    const handlePhotoInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ""; // cho phép chọn lại cùng file lần sau
        const cid = customer?.id;
        if (!cid || files.length === 0) return;
        // Kiểm tra định dạng + dung lượng theo giới hạn API
        const valid = files.filter((f) => {
            if (!f.type.startsWith("image/")) {
                message.error(t("customer.photo_invalid_type") || "Chỉ chấp nhận tệp hình ảnh");
                return false;
            }
            if (f.size > 10 * 1024 * 1024) {
                message.error(t("customer.photo_too_large", { name: f.name }) || `Ảnh "${f.name}" vượt quá 10MB`);
                return false;
            }
            return true;
        });
        if (valid.length === 0) return;
        setUploadingPhotos(true);
        const results = await Promise.all(valid.map((f) => uploadCustomerPhoto(cid, f)));
        const okCount = results.filter((r) => r.status).length;
        if (okCount > 0) {
            message.success(t("cd.photo_upload_success", { count: okCount }) || `Đã tải lên ${okCount} ảnh`);
            // Nạp lại danh sách để có URL/tiêu đề chuẩn từ server
            const res = await getCustomerPhotos(cid, { limit: 100 });
            if (res.status) setCustomerPhotos(res.items);
        }
        if (okCount < valid.length) {
            const firstErr = results.find((r) => !r.status)?.msg;
            message.error(firstErr || t("cd.photo_upload_failed") || "Tải ảnh lên thất bại");
        }
        setUploadingPhotos(false);
    };

    const handleDeletePhoto = async (photoId: number) => {
        const res = await deleteCustomerPhoto(photoId);
        if (res.status) {
            setCustomerPhotos((prev) => prev.filter((p) => p.id !== photoId));
            message.success(res.msg || t("cd.photo_delete_success") || "Đã xóa hình ảnh");
        } else {
            message.error(res.msg || t("cd.photo_delete_failed") || "Xóa hình ảnh thất bại");
        }
    };

    // ID khách hàng theo kênh (lấy connection đầu tiên có channel_customers_id) để load ảnh khách gửi
    const channelCustomerId =
        (customer?.connections as any[] | undefined)?.find((c) => c?.channel_customers_id)?.channel_customers_id ?? null;

    // Sync selected tags from customer data
    useEffect(() => {
        if (customer?.customer_tag_id) {
            setSelectedTagValues(prev => prev.length === 0 ? [customer.customer_tag_id!] : prev);
        }
    }, [customer?.customer_tag_id]);

    const tagOptions = customerTags.map(t => ({
        value: t.value,
        label: t.label,
        color: t.color || "#1890ff",
    }));

    const statusOptions = careStatuses.map(s => ({
        value: s.value,
        label: s.label,
        color: s.color || "#1890ff",
    }));

    const hasTags = selectedTagValues.length > 0;
    const getTagByValue = (val: number) => tagOptions.find(t => t.value === val);
    const tagInList = hasTags ? !!getTagByValue(selectedTagValues[0]) : false;
    const fallbackTag = !tagInList && customer?.customer_tag ? customer.customer_tag : null;

    // const handleSelectTag = async (val: number | null) => {
    //     setSelectedTagValues(val !== null ? [val] : []);
    //     setUpdatingField('customer_tag_id');
    //     try { await onUpdateField("customer_tag_id", val); } finally { setUpdatingField(null); }
    // };

    const currentCareStatusId = (customer as any)?.customer_care_status_id;
    const currentStatusOption = statusOptions.find(s => s.value === currentCareStatusId);

    const handleToggleStatus = async (val: number) => {
        setUpdatingField('customer_care_status_id');
        try { await onUpdateField("customer_care_status_id", val === currentCareStatusId ? null : val); } finally { setUpdatingField(null); }
    };

    const formatDate = (dateStr?: string) => {
        if (!dateStr) return "—";
        try {
            const d = new Date(dateStr);
            const now = new Date();
            const diffMs = now.getTime() - d.getTime();
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            if (diffDays === 0) return t('cd.today');
            return t('cd.days_ago', { count: diffDays });
        } catch { return dateStr; }
    };


    const [currentTagId, setCurrentTagId] = useState('S1');

    // Hàm xử lý khi người dùng chọn 1 mục
    const handleSelectTag = (val) => {
        setCurrentTagId(val);
        // Nếu có gọi API cập nhật thì bạn gọi ở đây
        // updateCustomerStatus(val); 
    };

    const tagOptions1 = [
        {
            value: 'S0',
            label: 'S0 - Khách hàng mới',
            color: '#3b82f6', // Màu xanh lam
        },
        {
            value: 'S1',
            label: 'S1 - Tò mò, nhận thức kém',
            color: '#475569', // Màu xám đen
        },
        {
            value: 'S2',
            label: 'S2 - Tìm hiểu sơ bộ',
            color: '#f59e0b', // Màu vàng cam
        },
        {
            value: 'S3',
            label: 'S3 - Có nhu cầu rõ ràng nhưng còn rào cản',
            color: '#d97706', // Màu cam đậm
        },
        {
            value: 'S4',
            label: 'S4 - Nhu cầu cao, cân nhắc lựa chọn',
            color: '#0ea5e9', // Màu xanh dương nhạt
        },
    ];
    return (
        <Card className="dark:bg-[#141414] dark:border-[#303030]">
            <Tabs
                className="dark:[&_.ant-tabs-tab]:text-gray-400 
                    dark:[&_.ant-tabs-tab-active]:text-yellow-400
                    dark:[&_.ant-tabs-ink-bar]:bg-yellow-400"
                defaultActiveKey="1"
                items={[
                    {
                        key: "1",
                        label: t('cd.tab_info'),
                        children: (
                            <>
                                {/* Yellow mini block */}
                                <div
                                    className="mb-4 p-4 rounded-md flex flex-col gap-2 
                                        bg-[#f6f1de] 
                                        dark:bg-[#1f1f1f] 
                                        text-000000e0"
                                >
                                    <div>{t('cd.last_contact')}
                                        <strong className="ml-1">{formatDate((customer as any)?.last_seen_at)}</strong>
                                    </div>
                                    <div>{t('cd.channel')}
                                        {customer?.connection ? (
                                            <Tag color="#1890ff" className="ml-1">
                                                {customer.connection.display_name}
                                            </Tag>
                                        ) : "—"}
                                    </div>

                                    <div className='flex gap-2 items-center min-w-0'>
                                        <span className="text-gray-700 text-000000e0 whitespace-nowrap">{t('cd.care_status')}</span>

                                        {fallbackTag ? (
                                            <EllipsisTag color={fallbackTag.color || "default"}>
                                                {fallbackTag.name || "—"}
                                            </EllipsisTag>
                                        ) : hasTags ? (
                                            <div className="flex items-center gap-2 min-w-0">
                                                <EllipsisTag color={getTagByValue(selectedTagValues[0])?.color || "default"}>
                                                    {getTagByValue(selectedTagValues[0])?.label || "—"}
                                                </EllipsisTag>

                                                <Dropdown
                                                    trigger={["click"]}
                                                    disabled={updatingField === 'customer_tag_id'}
                                                    menu={{
                                                        items: tagOptions.map((opt) => ({
                                                            key: opt.value,
                                                            label: (
                                                                <div className="flex items-center justify-between min-w-[140px]">
                                                                    <Tag color={opt.color} className="mr-0">{opt.label}</Tag>
                                                                    {selectedTagValues[0] === opt.value && (
                                                                        <CheckOutlined className="text-green-500 ml-2" />
                                                                    )}
                                                                </div>
                                                            ),
                                                            onClick: () => handleSelectTag(opt.value),
                                                        })),
                                                    }}
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={updatingField === 'customer_tag_id' ? <LoadingOutlined /> : <SyncOutlined className="text-gray-400 hover:text-blue-500" />}
                                                    />
                                                </Dropdown>
                                            </div>
                                        ) : (
                                            <Select
                                                size="small"
                                                placeholder="Chọn trạng thái"
                                                className="
                                                    w-full
                                                    dark:[&_.ant-select-selector]:bg-[#1f1f1f] 
                                                    dark:[&_.ant-select-selector]:border-[#303030]
                                                    dark:[&_.ant-select-selector]:text-gray-200
                                                "
                                                popupMatchSelectWidth={false}
                                                popupClassName="[&_.ant-select-item-option]:px-2 [&_.ant-select-item-option]:py-1.5"

                                                // 1. Ẩn dấu tick mặc định của Ant Design để chúng ta tự điều khiển
                                                menuItemSelectedIcon={<></>}

                                                // 2. Options chỉ chứa text để hiển thị trên ô Select gọn gàng
                                                options={tagOptions1.map((opt) => ({
                                                    value: opt.value,
                                                    label: opt.label,
                                                    color: opt.color,
                                                }))}

                                                // 3. Tùy biến lại giao diện dropdown: Khối màu + Dấu tick xanh
                                                optionRender={(option) => {
                                                    // Kiểm tra xem mục này có đang được chọn không bằng cách so sánh value
                                                    const isSelected = option.data.value === currentTagId;

                                                    return (
                                                        <div className="flex items-center justify-between w-full">
                                                            {/* Khối màu chiếm phần lớn diện tích (flex-1) */}
                                                            <div
                                                                style={{ backgroundColor: option.data.color }}
                                                                className="
                                                                    flex-1 
                                                                    text-white 
                                                                    px-3 
                                                                    py-1.5 
                                                                    rounded 
                                                                    font-medium 
                                                                    text-sm 
                                                                    truncate
                                                                "
                                                                title={option.data.label}
                                                            >
                                                                {option.data.label}
                                                            </div>

                                                            {/* Dấu tick xanh lá sẽ hiện ra nếu đang được chọn */}
                                                            {isSelected && (
                                                                <div className="pl-3 pr-1 text-emerald-500 font-bold text-base flex-shrink-0">
                                                                    ✓
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                }}

                                                value={currentTagId || undefined}
                                                onChange={(val) => handleSelectTag(val)}
                                            />
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-gray-700 text-000000e0 whitespace-nowrap">{t('cd.chat_tag')}</span>

                                        {currentCareStatusId && !currentStatusOption ? (
                                            <EllipsisTag color="default">
                                                {(customer as any)?.customer_care_status?.name || String(currentCareStatusId)}
                                            </EllipsisTag>
                                        ) : currentCareStatusId ? (
                                            <div className="flex items-center gap-2 min-w-0">
                                                <EllipsisTag color={currentStatusOption?.color || "default"}>
                                                    {currentStatusOption?.label || "—"}
                                                </EllipsisTag>

                                                <Dropdown
                                                    trigger={["click"]}
                                                    disabled={updatingField === 'customer_care_status_id'}
                                                    menu={{
                                                        items: statusOptions.map((opt) => ({
                                                            key: opt.value,
                                                            label: (
                                                                <div className="flex items-center justify-between min-w-[140px]">
                                                                    <Tag color={opt.color} className="mr-0">{opt.label}</Tag>
                                                                    {currentCareStatusId === opt.value && (
                                                                        <CheckOutlined className="text-green-500 ml-2" />
                                                                    )}
                                                                </div>
                                                            ),
                                                            onClick: () => handleToggleStatus(opt.value),
                                                        })),
                                                    }}
                                                >
                                                    <Button
                                                        type="text"
                                                        size="small"
                                                        icon={updatingField === 'customer_care_status_id' ? <LoadingOutlined /> : <SyncOutlined className="text-gray-400 hover:text-blue-500" />}
                                                    />
                                                </Dropdown>
                                            </div>
                                        ) : (
                                            <Select
                                                size="small"
                                                placeholder={t('cd.select_status')}
                                                loading={updatingField === 'customer_care_status_id'}
                                                className="w-[160px] dark:[&_.ant-select-selector]:bg-[#1f1f1f] 
                                                    dark:[&_.ant-select-selector]:border-[#303030]
                                                    dark:[&_.ant-select-selector]:text-gray-200"
                                                options={statusOptions.map(opt => ({
                                                    value: opt.value,
                                                    label: <Tag color={opt.color} className="mr-0">{opt.label}</Tag>
                                                }))}
                                                onChange={handleToggleStatus}
                                                value={null}
                                            />
                                        )}
                                    </div>

                                </div>

                                {/* Editable rows */}
                                <EditableRow
                                    label={t('cd.address')}
                                    fieldType="address"
                                    value={customer?.address || ""}
                                    provinceCode={customer?.province_code || ""}
                                    wardCode={customer?.ward_code || ""}
                                    onSaveAddress={onUpdateAddress}
                                />
                                <EditableRow
                                    label={t('cd.birthday')}
                                    fieldType="date"
                                    value={(customer as any)?.birthday || ""}
                                    onSave={(val) => onUpdateField("birthday", val)}
                                />
                                <EditableRow
                                    label={t('cd.gender')}
                                    fieldType="gender"
                                    value={(customer as any)?.gender || ""}
                                    onSave={(val) => onUpdateField("gender", val)}
                                />
                                <EditableRow
                                    label={t('cd.email')}
                                    value={(customer as any)?.email || ""}
                                    onSave={(val) => onUpdateField("email", val)}
                                />
                                <EditableRow
                                    label={t('cd.phone')}
                                    value={customer?.phone || ""}
                                    onSave={(val) => onUpdateField("phone", val)}
                                />
                                <EditableRow
                                    label={t('cd.occupation')}
                                    fieldType="select"
                                    value={customer?.customer_occupation_id ? String(customer.customer_occupation_id) : ""}
                                    options={occupations}
                                    onSave={(val) => onUpdateField("customer_occupation_id", Number(val))}
                                />
                                <EditableRow
                                    label={t('cd.notes')}
                                    fieldType="textarea"
                                    value={customer?.note || ""}
                                    onSave={(val) => onUpdateField("note", val)}
                                />

                                {/* Hình ảnh của khách hàng — bấm để phóng to, hover để xóa, ô cuối để tải thêm ảnh */}
                                <div className="py-3 border-b border-gray-100 dark:border-[#303030]">
                                    <div className="text-gray-500 dark:text-gray-400 text-sm mb-2">
                                        {t('cd.customer_photos')} ({customerPhotos.length})
                                    </div>
                                    <Image.PreviewGroup>
                                        <div className="flex flex-wrap gap-2">
                                            {customerPhotos.map((p) => (
                                                <div key={p.id} className="relative group">
                                                    <Image
                                                        src={p.image}
                                                        width={64}
                                                        height={64}
                                                        className="object-cover rounded-md border border-gray-200 dark:border-[#303030]"
                                                    />
                                                    <Popconfirm
                                                        title={t('cd.photo_delete_confirm') || 'Xóa hình ảnh này?'}
                                                        okText={t('notif.confirm_ok') || 'Đồng ý'}
                                                        cancelText={t('notif.confirm_cancel') || 'Hủy'}
                                                        okButtonProps={{ danger: true }}
                                                        onConfirm={() => handleDeletePhoto(p.id)}
                                                    >
                                                        <button
                                                            title={t('cd.photo_delete') || 'Xóa ảnh'}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[11px] leading-none hidden group-hover:flex items-center justify-center shadow hover:bg-red-600 z-10"
                                                        >
                                                            ✕
                                                        </button>
                                                    </Popconfirm>
                                                </div>
                                            ))}

                                            {/* Ô thêm ảnh: chọn được nhiều ảnh cùng lúc */}
                                            <button
                                                onClick={() => photoInputRef.current?.click()}
                                                disabled={uploadingPhotos}
                                                className="w-16 h-16 rounded-md border border-dashed border-gray-300 dark:border-[#3a3a3a] flex flex-col items-center justify-center text-gray-400 hover:border-[#3e91ab] hover:text-[#3e91ab] transition disabled:opacity-60 disabled:cursor-not-allowed"
                                            >
                                                {uploadingPhotos ? <LoadingOutlined /> : <PlusOutlined />}
                                                <span className="text-[10px] mt-0.5">{t('cd.photo_add') || 'Thêm ảnh'}</span>
                                            </button>
                                            <input
                                                ref={photoInputRef}
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                hidden
                                                onChange={handlePhotoInputChange}
                                            />
                                        </div>
                                    </Image.PreviewGroup>
                                </div>

                                <CallReportFail
                                    options={customer?.call_result_options}
                                    value={customer?.last_call_result ?? null}
                                    onReport={(val) => onUpdateField("last_call_result", val)}
                                />

                                <CustomerImgList channelCustomerId={channelCustomerId} />

                                <div
                                    className="
                                        d-none
                                        mt-4
                                        flex items-center justify-between
                                        px-4 py-3
                                        rounded-lg
                                        border border-gray-200
                                        bg-gray-50
                                        cursor-pointer
                                        transition
                                        hover:bg-gray-100
                                        dark:bg-[#1f1f1f]
                                        dark:border-[#303030]
                                        dark:hover:bg-[#262626]
                                    "
                                >
                                    <span className="font-medium text-gray-800 text-000000e0">
                                        {t('cd.dynamic_fields')}
                                    </span>

                                    <span className="text-gray-400 dark:text-gray-500 text-lg">
                                        <RightOutlined className="text-gray-400 dark:text-gray-500 text-[14px]" />
                                    </span>
                                </div>
                            </>
                        ),
                    },
                    {
                        key: "2",
                        label: <span className="d-none">{t('cd.tab_contact')}</span>,
                        children: (
                            <div className="d-none">
                                <ContactList />
                            </div>
                        ),
                    },
                    {
                        key: "3",
                        label: t('cd.tab_channel'),
                        children: (
                            <Channels />
                        ),
                    },
                ]}
            />
        </Card>
    );
}